import { SCRATCH_BRANCH } from 'app/branches/constants';
import { getProjectEndpoint } from 'app/branches/endpoints';
import {
  getCurrentBranchName,
  getSchemaHash,
  getScratchRuntimeSchema,
  getUseScratchForPreview
} from 'app/branches/selectors';
import useWatch from 'app/common/hooks/use-watch';
import { getToken } from 'app/common/utils/get-token';
import { getCurrentProjectId } from 'app/project/selectors';
import { buildClientSchema, type GraphQLSchema, getIntrospectionQuery } from 'graphql';
import { useCallback, useMemo, useState } from 'react';
import { useAsync } from 'react-async';
import { useSelector } from 'react-redux';

async function fetchIntrospection(projectId: string, branch: string): Promise<GraphQLSchema | null> {
  const endpoint = getProjectEndpoint(projectId, branch);
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-TakeShape-Token': getToken()!
    },
    body: JSON.stringify({ query: getIntrospectionQuery() })
  });

  if (res.status !== 200) {
    throw new Error(`Introspection failed with status ${res.status}`);
  }

  const { data } = await res.json();
  return data ? buildClientSchema(data) : null;
}

/**
 * Shared hook that provides an introspected GraphQL schema for the current project/branch.
 * Automatically re-introspects when the scratch schema changes.
 *
 * Used by both the API Explorer and the tool query editor.
 */
export function useIntrospectedSchema() {
  const projectId = useSelector(getCurrentProjectId);
  const currentBranchName = useSelector(getCurrentBranchName);
  const scratch = useSelector(getUseScratchForPreview);
  const currentSchemaHash = useSelector(getSchemaHash);
  const scratchSchema = useSelector(getScratchRuntimeSchema);

  const [schema, setSchema] = useState<GraphQLSchema | null>(null);

  const branch = useMemo(() => (scratch ? SCRATCH_BRANCH : currentBranchName), [scratch, currentBranchName]);
  const schemaHash = scratch ? undefined : currentSchemaHash;

  const introspect = useCallback(async () => {
    const result = await fetchIntrospection(projectId, branch);
    if (result) {
      setSchema(result);
    }
    return result;
  }, [projectId, branch]);

  // Re-introspect when scratch schema changes
  useWatch({
    value: scratchSchema,
    onChange: introspect
  });

  const { error: introspectionError, isPending: isIntrospecting } = useAsync({
    promiseFn: introspect
  });

  return {
    schema,
    branch,
    schemaHash,
    introspectionError,
    isIntrospecting
  };
}
